// src/components/subscription/SubscriptionManager.jsx
import React, { useState, useEffect } from 'react';
import {
  Crown,
  Calendar,
  Users,
  CreditCard,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Download,
  Settings,
  Upgrade,
  Brain,
  TrendingUp,
  Clock
} from 'lucide-react';
import { SubscriptionService } from '../../services/subscriptionService';
import { useAuth } from '../../contexts/AuthContext';
import PricingPlans from './PricingPlans';

const SubscriptionManager = () => {
  const { currentUser, userProfile } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [aiReportsUsage, setAIReportsUsage] = useState({});

  useEffect(() => {
    if (currentUser) {
      loadSubscriptions();
    }
  }, [currentUser]);

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      const userSubscriptions = await SubscriptionService.getUserSubscription(currentUser.uid);
      setSubscriptions(userSubscriptions);

      // Load AI reports usage for active subscriptions
      const usageData = {};
      for (const sub of userSubscriptions) {
        if (sub.status === 'active' && (sub.planId === 'ai-reports' || sub.planId.includes('annual'))) {
          const usage = await SubscriptionService.getAIReportsUsage(currentUser.uid, sub.planId);
          usageData[sub.id] = usage;
        }
      }
      setAIReportsUsage(usageData);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const getSubscriptionStatus = (subscription) => {
    const now = new Date();
    const expiresAt = new Date(subscription.expiresAt);
    const daysUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));

    if (subscription.status === 'active') {
      if (daysUntilExpiry <= 7) {
        return { status: 'expiring-soon', message: `Expires in ${daysUntilExpiry} days`, color: 'orange' };
      }
      return { status: 'active', message: 'Active', color: 'green' };
    }
    
    return { 
      status: subscription.status, 
      message: subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1),
      color: subscription.status === 'pending' ? 'blue' : 'red'
    };
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6e55a3]"></div>
      </div>
    );
  }

  const activeSubscriptions = subscriptions.filter(sub => sub.status === 'active');
  const hasActiveSubscription = activeSubscriptions.length > 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Crown className="w-6 h-6 text-[#6e55a3]" />
              Subscription Management
            </h1>
            <p className="text-gray-600 mt-1">Manage your TaskRegister subscription and billing</p>
          </div>
          
          <button
            onClick={loadSubscriptions}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview', icon: Crown },
              { id: 'usage', label: 'Usage & Reports', icon: Brain },
              { id: 'billing', label: 'Billing History', icon: CreditCard },
              { id: 'upgrade', label: 'Upgrade Plan', icon: TrendingUp }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-[#6e55a3] text-[#6e55a3]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {hasActiveSubscription ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {activeSubscriptions.map((subscription) => {
                    const statusInfo = getSubscriptionStatus(subscription);
                    
                    return (
                      <div key={subscription.id} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{subscription.planName}</h3>
                            <p className="text-gray-600 text-sm">{subscription.organizationName || 'Individual Plan'}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            statusInfo.color === 'green' ? 'bg-green-100 text-green-800' :
                            statusInfo.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                            statusInfo.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {statusInfo.message}
                          </span>
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Amount:</span>
                            <span className="font-medium">R{subscription.totalAmount}/month</span>
                          </div>
                          
                          {subscription.quantity > 1 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Educators:</span>
                              <span className="font-medium">{subscription.quantity}</span>
                            </div>
                          )}
                          
                          {subscription.learnerCount > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">AI Reports (Learners):</span>
                              <span className="font-medium">{subscription.learnerCount}</span>
                            </div>
                          )}
                          
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Started:</span>
                            <span className="font-medium">{formatDate(subscription.startDate)}</span>
                          </div>
                          
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Expires:</span>
                            <span className="font-medium">{formatDate(subscription.expiresAt)}</span>
                          </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex gap-2">
                            <button className="flex-1 bg-[#6e55a3] text-white px-4 py-2 rounded-md hover:bg-[#6156a6] text-sm font-medium">
                              Manage
                            </button>
                            <button className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 text-sm font-medium">
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Crown className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Subscription</h3>
                  <p className="text-gray-500 mb-6">
                    Subscribe to unlock the full power of TaskRegister with advanced features and AI-powered insights.
                  </p>
                  <button
                    onClick={() => setActiveTab('upgrade')}
                    className="bg-[#6e55a3] text-white px-6 py-3 rounded-md hover:bg-[#6156a6] font-medium"
                  >
                    Choose a Plan
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Usage Tab */}
          {activeTab === 'usage' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* AI Reports Usage */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Brain className="w-6 h-6 text-[#6e55a3]" />
                    <h3 className="font-semibold text-gray-900">AI Reports</h3>
                  </div>
                  
                  {Object.keys(aiReportsUsage).length > 0 ? (
                    <div className="space-y-2">
                      {Object.entries(aiReportsUsage).map(([subId, usage]) => (
                        <div key={subId}>
                          <div className="text-2xl font-bold text-[#6e55a3]">{usage}</div>
                          <div className="text-sm text-gray-600">reports generated this year</div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-[#6e55a3] h-2 rounded-full" 
                              style={{ width: `${Math.min((usage / 40) * 100, 100)}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {40 - usage} reports remaining
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">
                      No AI reports usage data available
                    </div>
                  )}
                </div>

                {/* Storage Usage */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Download className="w-6 h-6 text-green-600" />
                    <h3 className="font-semibold text-gray-900">Storage</h3>
                  </div>
                  <div className="text-2xl font-bold text-green-600">2.3 GB</div>
                  <div className="text-sm text-gray-600">of 10 GB used</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '23%' }}></div>
                  </div>
                </div>

                {/* Active Features */}
                <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="w-6 h-6 text-orange-600" />
                    <h3 className="font-semibold text-gray-900">Features</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Assessment Tasks</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Performance Analytics</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>AI Reports</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <div className="space-y-6">
              <div className="text-center py-12">
                <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Billing History</h3>
                <p className="text-gray-500">
                  Billing history and invoice management coming soon.
                </p>
              </div>
            </div>
          )}

          {/* Upgrade Tab */}
          {activeTab === 'upgrade' && (
            <div>
              <PricingPlans showCheckout={false} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManager;